import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, Layers, ShieldCheck, Truck, Boxes } from 'lucide-react';
import { MaskedLines, Reveal } from '../components/Motion';
import CTASection from '../components/CTASection';
import { IMAGES } from '../lib/data';

const PILLARS = [
  { icon: Layers, title: 'Complete requirements', desc: 'From stationery and marketing collateral to custom packaging and merchandise, we cover the full spectrum of business printing needs.' },
  { icon: ShieldCheck, title: 'Expertise & quality control', desc: 'Years of hands-on experience paired with careful checks at every stage of production keep quality consistent.' },
  { icon: Boxes, title: 'Reliable production partnerships', desc: 'Our in-house setup and trusted manufacturing partners give us the flexibility to handle orders of every scale.' },
  { icon: Truck, title: 'Sourcing, simplified', desc: 'Multiple solutions under one roof mean less vendor management and a single, dependable point of contact.' },
];

const VALUES = [
  'Honest, transparent communication',
  'Low minimum order quantities',
  'Flexible production capability',
  'Timely, nationwide delivery',
];

export default function About() {
  useEffect(() => {
    document.title = 'About Us | Shiva Prints & Packs — 10+ Years in Printing & Packaging';
  }, []);

  return (
    <>
      {/* Header */}
      <section data-testid="about-hero" className="bg-primary text-primary-foreground noise">
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pt-40 pb-24 md:pb-32">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} className="overline">
            About Shiva Prints &amp; Packs
          </motion.p>
          <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] max-w-4xl">
            <MaskedLines onLoad lines={['A decade of trusted', <>printing &amp; <span className="text-accent">packaging</span> partnership.</>]} />
          </h1>
        </div>
      </section>

      {/* Intro */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-2xl md:text-3xl font-display font-medium tracking-tight leading-snug">
                Shiva Prints &amp; Packs is a Delhi-based printing, packaging and physical branding
                solutions company, serving businesses across India.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-8 space-y-6 text-lg text-muted-foreground leading-relaxed">
                <p>
                  With over <span className="text-foreground font-medium">10 years of industry experience</span>, we help
                  businesses handle their complete printing and packaging requirements. Whether it&apos;s corporate stationery,
                  custom product boxes, marketing material or branded merchandise, our aim is to make sourcing simple and reliable.
                </p>
                <p>
                  We combine our own expertise and quality control with dependable manufacturing partnerships. This blend of
                  in-house production and trusted partners lets us stay flexible — supporting low minimum order quantities while
                  scaling comfortably for larger runs.
                </p>
                <p>
                  Our goal is straightforward: to be the one partner businesses can rely on for multiple solutions under one roof,
                  delivered with consistent quality and on-time, pan-India shipping.
                </p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="overflow-hidden">
                <img src={IMAGES.brochureMockup} alt="Printed brochures and corporate stationery" className="w-full h-[460px] object-cover hover:scale-105 transition-transform duration-700" />
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <ul className="mt-8 grid grid-cols-1 gap-3">
                {VALUES.map((v) => (
                  <li key={v} className="flex items-center gap-3 text-foreground">
                    <span className="flex h-6 w-6 items-center justify-center bg-accent/10 text-accent"><Check className="h-4 w-4" /></span>
                    {v}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="bg-muted/60">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <p className="overline">What defines our work</p>
            <h2 className="font-display mt-6 text-4xl md:text-5xl font-medium tracking-tight max-w-3xl">Expertise you can plan around</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.title} delay={i * 0.08} className="bg-card border border-border p-8 md:p-10">
                  <span className="flex h-12 w-12 items-center justify-center bg-primary text-primary-foreground"><Icon className="h-6 w-6" /></span>
                  <h3 className="font-display mt-6 text-2xl font-medium">{p.title}</h3>
                  <p className="mt-3 text-muted-foreground leading-relaxed">{p.desc}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <CTASection eyebrow="Work with us" title="Sourcing, simplified — under one roof." />
    </>
  );
}
